// Project sharing modal. Owner variant: members (with remove), pending invites
// (with revoke), and create-invite-link (role picker → copy URL). Member variant:
// "Shared by {owner} — you're an {role}" + Leave. Any sharing endpoint 404ing
// means the deployed backend predates sharing → single "isn't available yet"
// state (the 404 IS the feature flag; see the plan's old-backend degrade).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createInvite,
  leaveProject,
  listInvites,
  listMembers,
  removeMember,
  revokeInvite,
} from "../lib/apiClient";
import type { ProjectInvite } from "../lib/apiClient";
import { useStore } from "../lib/storeContext";
import { formatDate } from "../lib/util";

const is404 = (e: unknown) => e instanceof ApiError && e.status === 404;

export function SharePanel({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { store } = useStore();
  const membership = store.membership(projectId); // undefined ⇒ this is my own project

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="card max-h-[85vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-ink">Sharing</h2>
          <button className="btn-ghost px-3 py-1 text-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {membership ? (
          <MemberPanel
            projectId={projectId}
            ownerName={membership.ownerName}
            role={membership.role}
            onClose={onClose}
          />
        ) : (
          <OwnerPanel projectId={projectId} />
        )}
      </div>
    </div>
  );
}

function SharingUnavailable() {
  return (
    <p className="rounded-card bg-cream px-4 py-3 text-sm text-muted">
      Sharing isn&rsquo;t available yet.
    </p>
  );
}

// --- Owner variant ---

function OwnerPanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [created, setCreated] = useState<ProjectInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const members = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: () => listMembers(projectId),
    retry: false,
  });
  const invites = useQuery({
    queryKey: ["project-invites", projectId],
    queryFn: () => listInvites(projectId),
    retry: false,
  });

  // Old backend: sharing routes don't exist yet — one calm state, no error spam.
  if (is404(members.error) || is404(invites.error)) return <SharingUnavailable />;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const handleCreate = () =>
    run(async () => {
      const invite = await createInvite(projectId, role, expiresInDays);
      setCreated(invite);
      setCopied(false);
      await queryClient.invalidateQueries({ queryKey: ["project-invites", projectId] });
    });

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Couldn't copy — long-press or right-click the link instead.");
    }
  };

  const handleRevoke = (token: string) =>
    run(async () => {
      if (created?.token === token) setCreated(null);
      await revokeInvite(token);
      await queryClient.invalidateQueries({ queryKey: ["project-invites", projectId] });
    });

  const handleRemove = (userId: string) =>
    run(async () => {
      await removeMember(projectId, userId);
      await queryClient.invalidateQueries({ queryKey: ["project-members", projectId] });
    });

  const activeInvites = (invites.data ?? []).filter(
    (i) => (i.status ?? "active") === "active" && !i.revokedAt,
  );

  return (
    <div className="space-y-6">
      {/* Create invite link */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink">Invite someone</h3>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-lg border border-black/10 px-2 py-1.5 text-sm"
            value={role}
            disabled={busy}
            onChange={(e) => setRole(e.target.value as "editor" | "viewer")}
          >
            <option value="editor">Editor — can add & edit</option>
            <option value="viewer">Viewer — read-only</option>
          </select>
          <select
            className="rounded-lg border border-black/10 px-2 py-1.5 text-sm"
            value={expiresInDays}
            disabled={busy}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
          >
            <option value={7}>Expires in 7 days</option>
            <option value={30}>Expires in 30 days</option>
          </select>
          <button className="btn-warm px-3 py-1.5 text-sm" onClick={handleCreate} disabled={busy}>
            {busy ? "…" : "Create invite link"}
          </button>
        </div>
        {created && (
          <div className="mt-3 flex items-center gap-2 rounded-card bg-cream px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-xs text-ink" title={created.url}>
              {created.url}
            </span>
            <button
              className="btn-ghost shrink-0 px-3 py-1 text-xs"
              onClick={() => handleCopy(created.url)}
            >
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
        )}
      </section>

      {/* Members */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink">Members</h3>
        {members.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : members.error ? (
          <p className="text-sm text-red-500">Couldn&rsquo;t load members.</p>
        ) : (members.data ?? []).length === 0 ? (
          <p className="text-sm text-muted">Just you so far — share a link to invite someone.</p>
        ) : (
          <ul className="space-y-2">
            {(members.data ?? []).map((m) => (
              <li key={m.userId} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">
                    {m.name || m.email || "Member"}
                  </div>
                  {m.name && m.email && <div className="truncate text-xs text-muted">{m.email}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="chip capitalize">{m.role}</span>
                  {m.role !== "owner" && (
                    <button
                      className="btn-ghost px-2 py-1 text-xs text-red-600"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm(`Remove ${m.name || m.email || "this member"} from the project?`)) {
                          handleRemove(m.userId);
                        }
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-ink">Pending invites</h3>
        {invites.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : invites.error ? (
          <p className="text-sm text-red-500">Couldn&rsquo;t load invites.</p>
        ) : activeInvites.length === 0 ? (
          <p className="text-sm text-muted">No active invite links.</p>
        ) : (
          <ul className="space-y-2">
            {activeInvites.map((i) => (
              <li key={i.token} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm text-ink capitalize">{i.role} link</div>
                  <div className="text-xs text-muted">Expires {formatDate(i.expiresAt)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button className="btn-ghost px-2 py-1 text-xs" onClick={() => handleCopy(i.url)}>
                    Copy
                  </button>
                  <button
                    className="btn-ghost px-2 py-1 text-xs text-red-600"
                    disabled={busy}
                    onClick={() => handleRevoke(i.token)}
                  >
                    Revoke
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {err && <p className="text-sm text-red-500">{err}</p>}
    </div>
  );
}

// --- Member (non-owner) variant ---

function MemberPanel({
  projectId,
  ownerName,
  role,
  onClose,
}: {
  projectId: string;
  ownerName: string | null;
  role: string;
  onClose: () => void;
}) {
  const { store, touch } = useStore();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  async function handleLeave() {
    if (!window.confirm("Leave this project? You'll lose access until you're invited again.")) return;
    setBusy(true);
    setErr(null);
    try {
      await leaveProject(projectId);
    } catch (e) {
      if (is404(e)) {
        setUnavailable(true);
        setBusy(false);
        return;
      }
      // A 403 means we're already not a member (revoked) — fall through and purge.
      if (!(e instanceof ApiError && e.status === 403)) {
        setErr(e instanceof Error ? e.message : "Couldn't leave the project.");
        setBusy(false);
        return;
      }
    }
    store.purgeScope(projectId);
    touch();
    onClose();
    navigate("/");
  }

  if (unavailable) return <SharingUnavailable />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink">
        Shared by <span className="font-semibold">{ownerName || "the owner"}</span> — you&rsquo;re
        {role === "editor" ? " an " : " a "}
        <span className="font-semibold capitalize">{role}</span>.
      </p>
      <p className="text-xs text-muted">
        {role === "editor"
          ? "You can add and edit rooms, photos, scans, and makeovers. Only the owner can delete the project or manage sharing."
          : "You have read-only access to this project."}
      </p>
      <button className="btn-ghost text-sm text-red-600" onClick={handleLeave} disabled={busy}>
        {busy ? "Leaving…" : "Leave project"}
      </button>
      {err && <p className="text-sm text-red-500">{err}</p>}
    </div>
  );
}
